import { useEffect } from 'react';
import { Button, makeStyles, Grid, Box, Link } from '@material-ui/core';
import { toast } from 'react-toastify';
import { useAppContext } from '../../core/contexts';
import { useHistory } from 'react-router';
import { getMailToResetPassword } from 'core/services/auth';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SGSForm, SGSFormInput } from 'components';
import SmallHeader from 'features/utilisateurs/components/header';
import SmallFooter from 'features/utilisateurs/components/Footer';
import secure from '../../assets/secure.svg';
import welcome from '../../assets/welcome.svg';
import classNames from 'classnames';

const ResetPassword = () => {
    const history = useHistory();
    const { setAppLoading } = useAppContext();
    const classes = useStyles();

    useEffect(() => setAppLoading(false), [setAppLoading]);

    type Email = {
        email: string;
    };

    const validationSchema: Yup.SchemaOf<Email> = Yup.object().shape({
        email: Yup.string().email('Email invalide !').required(),
    });

    const methods = useForm<Email>({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });

    const { handleSubmit } = methods;

    const handleSendForm = (form: Email) => {
        setAppLoading(true);
        getMailToResetPassword({ email: form.email })
            .then(() => {
                toast.info('Un email vous a été envoyé !');
                signin();
            })
            .catch((err: string) => toast.error(err))
            .finally(() => setAppLoading(false));
    };

    const signin = () => history.push('/connexion');

    return (
        <Box className={classes.container}>
            <SmallHeader />
            <Box className={`${classes.row} ${classes.middleRow}`}>
                <Grid container justifyContent="center">
                    <Grid item md={2} className={'p-5'}></Grid>

                    <Grid item md={5} className={classes.boxRight}>
                        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                            <Box className={classes.logoSecure}>
                                <img src={secure} alt={''} />
                            </Box>
                            <Box className="ml-3">
                                <Box className={classes.sousTitre}>Mot de passe oublié</Box>
                            </Box>
                        </Box>

                        <Box className={classes.txt}>
                            <Box>Entrez votre adresse e-mail pour recevoir un lien sécurisé de réinitialisation de mot de passe.</Box>

                            <Box className="mt-4">
                                <Box className={classes.txtBlueBold}>Email :</Box>
                                <FormProvider {...methods}>
                                    <form onSubmit={handleSubmit(handleSendForm)} className={classes.form}>
                                        <SGSForm.Row>
                                            <SGSForm.Col>
                                                <SGSFormInput variant="outlined" name="email" uppercase />
                                            </SGSForm.Col>
                                        </SGSForm.Row>

                                        <Button fullWidth type="submit" variant="contained" color="primary" className={classes.submit}>
                                            réinitialiser
                                        </Button>
                                    </form>
                                </FormProvider>
                            </Box>
                            <Box className="mt-4">
                                En cas de problème, vous pouvez nous contacter à l'adresse{' '}
                                <Link href="mailto:support@sgsante.fr" className={classes.emailLink}>
                                    support@sgsante.fr
                                </Link>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item md={3} className={classNames(classes.mobile, 'p-5')}>
                        <Box>
                            <img src={welcome} alt={''} />
                        </Box>
                    </Grid>

                    <Grid item md={2} className={'p-5'}></Grid>
                </Grid>
            </Box>
            <SmallFooter />
        </Box>
    );
};

export default ResetPassword;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
    },
    row: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid #ccc',
    },
    middleRow: {
        flex: 2,
    },
    titre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: '36px',
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    sousTitre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: 600,
        marginTop: '10px',
        lineHeight: 'normal',
    },

    txt: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 300,
        maxWidth: '700px',
        marginTop: '30px',
        textAlign: 'justify',
    },
    txtBlueBold: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 600,
        maxWidth: '700px',
        marginTop: '10px',
        marginBottom: '10px',
    },
    paper: {
        paddingTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        color: '#FFF',
        textAlign: 'center',
        fontFamily: 'Open Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 700,
        padding: '16px',
        borderRadius: '10px',
    },
    contactLink: {
        textDecoration: 'none', // Optional: Remove underline
        color: theme.palette.primary.main, // Optional: Use primary color
    },
    boxRight: {
        background: '#fff',
        paddingLeft: '50px',
        paddingRight: '50px',
        borderRadius: '10px 10px 0px 0px',
        maxWidth: '582px',
        padding: '20px',
        paddingBottom: '40px',
    },
    logoSecure: {
        background: '#fff',
        borderRadius: '50%',
        marginTop: '-80px',
        padding: '20px',
    },
    txtRed: {
        color: '#EB4C37',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtGreen: {
        color: '#46BE81',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtBlue: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    txtBlueUnderline: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
        textDecorationLine: 'underline',
    },
    emailLink: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: 18,
        fontStyle: 'normal', // You can adjust this to 'italic' if needed
        fontWeight: 700,
        lineHeight: '26px',
        textDecorationLine: 'underline',
    },
    mobile: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
}));
